import {GameStateEnums, NetworkEnums, PlayerEnums} from '../../../../shared/GameEnums'
import {BeginnerHintFormat, BuildGameConfigurations, GameEndData, SelectDiceFormat, SelectPlayerFormat} from '@colonist/game-base-types'
import {MatchmakingStatusUpdate} from '../../../../shared/matchmaking/MatchmakingInterfaces'
import {RoomState} from '../../../../shared/RoomState'
import {SocketDataTypes} from '../../../../shared/SocketDataTypes'
import {ChatData} from '@colonist/shared-types'
import {BaseError} from '../../../../shared/errors/BaseError'
import {
    SelectCardPopupUsingDevelopmentCardFormat, SelectCardPopupForPendingDistributionFormat, SelectCardPopupStealCardFromAnotherPlayerFormat,
    SelectCardToDiscardFormat, SelectCardsToGiveToAnotherPlayer, SelectPlayerUsingDevelopmentCardFormat, SelectPlayerAndResourceCardFormat,
    CardExchangeFormat,
    CardPopupInformation,
} from '@colonist/game-card-types'
import {UIGameManager} from '../ui_game/UIGameManager'
import {CardEnum, GameChatData, GameChatState, GameLogData, GameLogState, MechanicBarbarianInvasionState, MechanicKnightState, NestedPartial, TradeId, TradeState, isFullTradeState} from '@colonist/game-states'
import {SocketLobbySend} from './SocketLobbySend'
import {CA} from '../analytics/Analytics'
import {FriendRoomInviteNotificationData, RoomInviteResponse, UpdateOnlineStatusData, FriendRequestReceivedData} from '../../../../shared/friends/FriendTypes'
import {UIGameEndController} from '../ui_game/ui_game_end_screen/UIGameEndController'
import {MarkedAsReadData, NotificationDataWithUnreadCount} from '../../../../shared/NotificationTypes'
import {RoomPlayOrderSelectionData, RoomSeedOrderSelectionData} from '@colonist/lobby-types'
import RoomStateGotKickedData = NetworkEnums.RoomStateGotKickedData
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {GameDataFirstGameState, VoteState, BankTransactionLedger, PauseState, BeginnerModeActionState, SocketGameStateUpdate} from '@colonist/game-state-types'
import {UIGeneralServicesController} from '../UIGeneralServicesController'
import {addNewLog, createFeedMessageDataForGameChatData, setGlobalGameState, setGlobalIsPaused, setGlobalRemainingPauseTimeMS, setGlobalVoteState} from '@colonist/ui-game'
import {notificationController} from '../EntryShared'
import {TranslationObject} from '@colonist/i18n'
import {UITopNotificationRateResponseValue, UITopNotificationType} from '../notifications/UINotificationController'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../shared/entity_datas/EntityDatas'
import {TimeUtils} from '@colonist/utils'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'

export abstract class SocketActionController {
    protected readonly generalServicesController: UIGeneralServicesController
    abstract readonly uiGameManager: UIGameManager | undefined

    constructor(generalServicesController: UIGeneralServicesController) {
        this.generalServicesController = generalServicesController
    }

    abstract onChatText(data: ChatData)
    abstract onStateSessionState()
    abstract onStateCustomRoom(data: RoomState)
    abstract onStateShowPremiumPopup()
    abstract onSeedOrderSelection(data: RoomSeedOrderSelectionData)
    abstract onPlayOrderSelection(data: RoomPlayOrderSelectionData)
    abstract onStateDisplayInfoMessage(data: PlayerEnums.RoomInfoMessage)

    // eslint-disable-next-line class-methods-use-this
    onStateAutoRejoinGame(gameId: string) {
        SocketLobbySend.reconnectGame(gameId, CA.Label.AutoRejoinGame)
    }

    abstract onStateGotKicked(data: RoomStateGotKickedData)
    abstract onRoomListUpdate(data: NetworkEnums.RoomListUpdateData)

    abstract onConnectionEstablished(data: GameDataFirstGameState)
    abstract onStateInitialPlacement()

    onStateCurrentGameState() {
        this.uiGameManager?.eventController.sendEvent(undefined, GameStateEnums.UIGameEventEnums.GameStateUpdated)
    }
    onStateBankState() {
        this.uiGameManager?.eventController.sendEvent(undefined, UIGameEventEnums.BankStateUpdated)
    }
    onStateMapState(animate: boolean) {
        this.uiGameManager?.eventController.sendEvent(animate, UIGameEventEnums.MapStateUpdated)
    }
    onPlayerControllerState(animate: boolean) {
        this.uiGameManager?.eventController.sendEvent(animate, UIGameEventEnums.PlayerControllerStateUpdated)
    }
    // eslint-disable-next-line class-methods-use-this
    onVote(data: VoteState) {
        setGlobalVoteState(data)
    }
    onSelectPlayer(data: SelectPlayerFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SelectPlayer)
    }
    onGivePlayerResourcesFromTile(data: BankTransactionLedger[]) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.GivePlayerCardFromTile)
    }
    onExchangeCards(data: CardExchangeFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.ExchangeCards)
    }
    onHighlightCorners(data: number[]) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SetCornerHighlight)
    }
    onHighlightRoadEdges(data: number[]) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SetRoadEdgeHighlight)
    }
    onHighlightShipEdges(data: number[]) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SetShipEdgeHighlight)
    }
    onHighlightTiles(data: number[]) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SetTileHighlight)
    }
    onHighlightDiceNumbersForInventor(data: number[]) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.HighlightDiceNumbersForInventor)
    }
    onAmountOfCardsToDiscard(data: SelectCardToDiscardFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SelectCardsToDiscard)
    }
    onSelectCardsToGiveToPlayer(data: SelectCardsToGiveToAnotherPlayer) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SelectCardToGiveToAnotherPlayer)
    }
    onSelectPlayerAndCard(data: SelectPlayerAndResourceCardFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SelectPlayerAndCard)
    }
    onCardsToSelectForPendingTransaction(data: SelectCardPopupForPendingDistributionFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SelectCardsForPendingDistribution)
    }
    onConfirmUseDevelopmentCard(data: CardPopupInformation) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.ConfirmUseDevelopmentCard)
    }
    onSelectPlayerUsingDevelopmentCard(data: SelectPlayerUsingDevelopmentCardFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SelectPlayerForDevelopmentCard)
    }
    onStealDevelopmentCardFromPlayer(data: SelectCardPopupStealCardFromAnotherPlayerFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.StealDevelopmentCardFromPlayer)
    }
    onStealResourceCardFromPlayer(data: SelectCardPopupStealCardFromAnotherPlayerFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.StealResourceCardFromPlayer)
    }
    onClosePopupUI() {
        this.uiGameManager?.eventController.sendEvent(undefined, UIGameEventEnums.ClosePopupUI)
    }
    onCardsToSelectForDevelopmentCard(data: SelectCardPopupUsingDevelopmentCardFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SelectCardsFromBankUsingDevelopmentCard)
    }
    onSelectDice(data: SelectDiceFormat) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.SelectDice)
    }
    onCityImprovementStateUpdated() {
        this.uiGameManager?.eventController.sendEvent(undefined, UIGameEventEnums.CityImprovementStateUpdated)
    }
    onSpecialBuildPhaseUpdated() {
        this.uiGameManager?.eventController.sendEvent(undefined, UIGameEventEnums.SpecialBuildPhaseStateUpdated)
    }
    onForceShowCityImprovementButtons() {
        this.uiGameManager?.eventController.sendEvent(undefined, UIGameEventEnums.ForceShowCityImprovementButtons)
    }
    onKnightStateUpdated(knightState: NestedPartial<MechanicKnightState>) {
        this.uiGameManager?.eventController.sendEvent(knightState, UIGameEventEnums.KnightStateUpdated)
    }
    onBarbarianStateUpdated(barbarianInvasionState: NestedPartial<MechanicBarbarianInvasionState>) {
        this.uiGameManager?.eventController.sendEvent(barbarianInvasionState, UIGameEventEnums.BarbarianInvasionStateUpdated)
    }
    onEmbargoState() {
        this.uiGameManager?.eventController.sendEvent(undefined, UIGameEventEnums.EmbargoStatusUpdated)
    }
    onCloseOffer(tradeId: TradeId) {
        this.uiGameManager?.eventController.sendEvent(tradeId, UIGameEventEnums.RemoveTradeOffer)
    }
    onResetTradeStateAtEndOfTurn() {
        this.uiGameManager?.eventController.sendEvent(undefined, UIGameEventEnums.TradeResetStateAtEndOfTurn)
    }
    onTradeResponseState(tradeId: TradeId) {
        this.uiGameManager?.eventController.sendEvent(tradeId, UIGameEventEnums.TradeResponseState)
    }
    onTradePlayersCreatingCounterOfferState(tradeId: TradeId) {
        this.uiGameManager?.eventController.sendEvent(tradeId, UIGameEventEnums.TradePlayersCreatingCounterOfferState)
    }
    onGetOffer(tradeId: TradeId) {
        this.uiGameManager?.eventController.sendEvent(tradeId, UIGameEventEnums.NewTradeOfferReceived)
    }
    onBoldTextInTradeWindow(cardsToBold: CardEnum[]) {
        this.uiGameManager?.eventController.sendEvent(cardsToBold, UIGameEventEnums.BoldTextInTradeWindow)
    }
    onBuildGame(configurations: BuildGameConfigurations) {
        this.onStateInitialPlacement()
        if(this.uiGameManager == undefined) return
        this.uiGameManager.buildGame(configurations)
        if(this.uiGameManager.chatController != undefined) {
            this.uiGameManager.chatController.buildGameLogs(configurations.gameState.gameLogState)
            this.uiGameManager.chatController.buildGameChat(configurations.gameState.gameChatState)
        }
        CA.sendGoogleTagManagerEvent(CA.GoogleTagManagerEvent.GameStarted)
        this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.FinishedBuildingGame)
    }

    onDiceState(animate: boolean = true) {
        this.uiGameManager?.eventController.sendEvent(animate, UIGameEventEnums.DiceStateUpdated)
    }

    onGameEndState(gameEndData: GameEndData, isShownAfterLiveGame?: boolean): UIGameEndController | undefined {
        if(this.uiGameManager?.gameController.isReplay) return undefined
        if(this.uiGameManager?.gameController.isBotGame()) return undefined
        if(this.uiGameManager?.gameController.isPlayerSpectator) return undefined
        if(UIUtilsEjsVariables.isUserOnDiscordMobile) return undefined

        const numberOfChatMessages = this.uiGameManager?.gameController.getNumberOfChatMessages() ?? 0
        if(numberOfChatMessages < 2) return undefined

        const rateChatExperienceText: TranslationObject = {key: 'strings:popups.notification.chatExperience.question'}
        notificationController.showRate(rateChatExperienceText, (responseValue: UITopNotificationRateResponseValue) => {
            const value = responseValue === 'positive' ? 1 : 0
            void CA.sendAnalyticEventsWithMobileDimensions({
                type: AnalyticEventType.RateChatExperience,
                category: AnalyticEventCategory.Chat,
                dimensions: {
                    chatExperience: value,
                },
            })

            const rateChatExperienceThanksText: TranslationObject = {key: 'strings:popups.notification.chatExperience.thanks'}
            const showCloseButton = true
            const duration = 3 * TimeUtils.MILLISECONDS
            notificationController.show(rateChatExperienceThanksText, UITopNotificationType.Information, undefined, undefined, undefined, showCloseButton, duration)
        })
        return undefined
    }

    onEndGameText(data: any) {
        const playersJoinedRematch: number = data
        this.uiGameManager?.eventController.sendEvent(playersJoinedRematch, UIGameEventEnums.GameEndRematchTextUpdated)
    }

    onTogglePauseGame(data: any) {
        const pauseState = Object.assign(new PauseState(), data) as PauseState
        this.uiGameManager?.pauseController.togglePause(pauseState)
        setGlobalIsPaused(pauseState.paused)
        setGlobalRemainingPauseTimeMS(pauseState.remainingPauseTime)
    }

    abstract onMatchmakingUpdate(matchmakingStatusUpdate: MatchmakingStatusUpdate)
    abstract onMatchmakingError(error: BaseError)
    abstract onMatchmakingShowPreRollAd()
    abstract onMatchmakingDisableAdblock()

    abstract onChannelRoomUpdate(roomData: SocketDataTypes.ChannelRoomUpdate)

    onBeginnerHintActivated(suggestedMove: BeginnerHintFormat) {
        this.uiGameManager?.eventController.sendEvent(suggestedMove, UIGameEventEnums.BeginnerHintReceived)
    }

    onBeginnerModeActionUpdate(data: BeginnerModeActionState) {
        this.uiGameManager?.eventController.sendEvent(data, UIGameEventEnums.BeginnerModeActionUpdated)
    }

    onBeginnerModeLevelEnd() {
        this.uiGameManager?.eventController.sendEvent(undefined, UIGameEventEnums.BeginnerModeLevelEnd)
    }

    gameStateUpdated(timedGameStateUpdate: SocketGameStateUpdate, animate: boolean = true) {
        if(this.uiGameManager === undefined) return

        const gameStateUpdate = timedGameStateUpdate.diff
        this.uiGameManager.gameController.applyStateUpdates([gameStateUpdate])
        this.uiGameManager.gameController.timeLeftInCurrentState = timedGameStateUpdate.timeLeftInState

        if(gameStateUpdate.currentState != undefined) this.onStateCurrentGameState()

        // Event dice was broken off of dice state but for now they are always updated together so combining them is fine
        const shouldUpdateDiceState: boolean = gameStateUpdate.diceState != undefined
            || gameStateUpdate.mechanicEventDiceState != undefined

        if(shouldUpdateDiceState) this.onDiceState(animate)

        // Many states were removed from the player state into their own state. But the listeners were not updated to be independent in the UI.
        // So many independent states still need to call `onPlayerControllerState` to avoid breaking things in the UI

        const shouldUpdatePlayerState: boolean = gameStateUpdate.playerStates != undefined
            || gameStateUpdate.mechanicDevelopmentCardsState != undefined
            || gameStateUpdate.mechanicSettlementState != undefined
            || gameStateUpdate.mechanicCityState != undefined
            || gameStateUpdate.mechanicCityWallState != undefined
            || gameStateUpdate.mechanicRoadState != undefined
            || gameStateUpdate.mechanicShipState != undefined
            || gameStateUpdate.mechanicProgressCardsState != undefined
            || gameStateUpdate.mechanicLongestRoadState != undefined
            || gameStateUpdate.mechanicLargestArmyState != undefined
        if(shouldUpdatePlayerState) this.onPlayerControllerState(animate)
        // end of section that needs to update player controller state

        if(gameStateUpdate.bankState != undefined) this.onStateBankState()
        if(gameStateUpdate.tradeState != undefined) this.onTradeStateUpdate(gameStateUpdate.tradeState)

        // A few states were pulled out of map. To make the refactor easier they just all call the old map state logic
        const shouldUpdateMapState: boolean = gameStateUpdate.mapState != undefined
            || gameStateUpdate.mechanicRobberState != undefined
            || gameStateUpdate.mechanicPirateState != undefined
            || gameStateUpdate.mechanicMerchantState != undefined
        if(shouldUpdateMapState) this.onStateMapState(animate)
        if(gameStateUpdate.gameLogState != undefined) this.onGameLogStateUpdate(gameStateUpdate.gameLogState)
        if(gameStateUpdate.gameChatState != undefined) this.onGameChatStateUpdate(gameStateUpdate.gameChatState)
        if(gameStateUpdate.mechanicSpecialBuildPhaseState) this.onSpecialBuildPhaseUpdated()
        if(gameStateUpdate.mechanicKnightState) this.onKnightStateUpdated(gameStateUpdate.mechanicKnightState)
        if(gameStateUpdate.mechanicBarbarianInvasionState) this.onBarbarianStateUpdated(gameStateUpdate.mechanicBarbarianInvasionState)
        if(gameStateUpdate.mechanicCityImprovementsState) this.onCityImprovementStateUpdated()

        setGlobalGameState(this.uiGameManager.gameController['stateController']['state'])
    }

    onTradeStateUpdate(tradeState: NestedPartial<TradeState>) {
        if(tradeState.embargoState != undefined) this.onEmbargoState()

        if(tradeState.activeOffers != undefined) {
            for(const tradeId in tradeState.activeOffers) {
                const tradeStateUpdate = tradeState.activeOffers[tradeId]

                const isTradeClosed = tradeStateUpdate === null
                if(isTradeClosed) {
                    this.onCloseOffer(tradeId)
                    continue
                }

                if(tradeStateUpdate == undefined) continue

                const isPartialTradeStateUpdate = Object.keys(tradeStateUpdate).length == 1
                if(isPartialTradeStateUpdate) {
                    if(tradeStateUpdate.playerResponses != undefined) this.onTradeResponseState(tradeId)
                    if(tradeStateUpdate.playersCreatingCounterOffer != undefined) this.onTradePlayersCreatingCounterOfferState(tradeId)

                    continue
                }

                if(isFullTradeState(tradeStateUpdate)) this.onGetOffer(tradeId)
            }
        }

        this.uiGameManager?.eventController.sendEvent(undefined, UIGameEventEnums.TradeStateUpdated)
    }

    onGameLogStateUpdate(gameLogsUpdate: NestedPartial<GameLogState>) {
        if(this.uiGameManager === undefined) return
        if(this.uiGameManager.gameController.isReplay) return

        for(const gameLogIndex in gameLogsUpdate) {
            const gameLogData = gameLogsUpdate[gameLogIndex] as GameLogData | undefined
            if(gameLogData == undefined) continue

            this.uiGameManager?.chatController?.writeToLog({index: Number(gameLogIndex), gameLogData: gameLogData})
            addNewLog(Number(gameLogIndex), gameLogData)
        }
    }

    onGameChatStateUpdate(gameChatUpdate: NestedPartial<GameChatState>) {
        if(this.uiGameManager === undefined) return
        for(const gameChatIndex in gameChatUpdate) {
            const gameChatData = gameChatUpdate[gameChatIndex] as GameChatData | undefined

            const wasChatMessageDeleted = gameChatData === null
            if(wasChatMessageDeleted) {
                this.uiGameManager.chatController?.clearGameChatWithIndexGreaterThanOrEqualTo(Number(gameChatIndex))
                continue
            }

            if(gameChatData == undefined) continue

            const chatData = createFeedMessageDataForGameChatData(this.uiGameManager.gameController.gameValidator, gameChatData.text)
            this.onChatText(chatData)
        }
    }

    onFriendUpdateOnlineStatus(data: UpdateOnlineStatusData) {
        if(data.sendingAllFriends) return this.generalServicesController.onlineFriendController?.updateAllFriendsOnlineStatus(data.onlineFriendStatuses)
        const singleFriendData = data.onlineFriendStatuses[0]
        this.generalServicesController.onlineFriendController?.updateFriendOnlineStatus(singleFriendData)
    }

    onFriendReceivedRoomInvites(roomInvites: FriendRoomInviteNotificationData[]) {
        this.generalServicesController.onlineFriendController?.receivedRoomInvites(roomInvites)
    }

    onFriendReceivedFriendRequest(friendRequest: FriendRequestReceivedData) {
        this.generalServicesController.onlineFriendController?.receivedFriendRequest(friendRequest)
    }

    onFriendRemoveRoomInvite(roomId: string) {
        this.generalServicesController.onlineFriendController?.removeRoomInvitesWithRoomId(roomId)
    }

    onFriendRoomInviteResponse(data: RoomInviteResponse) {
        this.generalServicesController.onlineFriendController?.friendRespondedToRoomInvite(data)
    }

    onFriendRoomInviteSentSuccessfully(receiverUserId: string) {
        this.generalServicesController.onlineFriendController?.roomInviteSentToFriendSuccessfully(receiverUserId)
    }

    onNotificationsReceived(notificationDataWithUnreadCount: NotificationDataWithUnreadCount) {
        this.generalServicesController.notificationActionController?.addNotificationsToPanel(notificationDataWithUnreadCount)
    }

    onMarkedAsRead(markedAsReadData: MarkedAsReadData) {
        this.generalServicesController.notificationActionController?.markNotificationAsRead(markedAsReadData)
    }
}
